import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useTokenOfferBelongsToUser } from '@sorare/core/src/hooks/useTokenOfferBelongsToUser';

import { TokenDetailsRow } from '#src/components/ItemPreview/ui.tsx';
import { FeesDetailsTooltip } from '#src/components/offer/FeesDetailsTooltip/index.tsx';

import type { SalePrice_offer } from './__generated__/index.graphql.ts';

const StyledTokenDetailsRow = styled(TokenDetailsRow)`
  justify-content: initial;
`;

type Props = {
  sale: SalePrice_offer;
  showFees?: boolean;
};

export const SalePrice = ({ sale, showFees }: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const belongsToUser = useTokenOfferBelongsToUser();
  const saleBelongsToUser = belongsToUser(sale);

  const { marketFeeAmounts } = sale;

  const offerAmounts = toMonetaryAmount(sale.receiverSide.amounts);
  const marketFeeMonetaryAmount = toMonetaryAmount(marketFeeAmounts);
  const hasFees = marketFeeMonetaryAmount?.isPositive;

  return (
    <StyledTokenDetailsRow>
      <AmountWithConversion monetaryAmount={offerAmounts} />
      {!!showFees && !!saleBelongsToUser && !!hasFees && (
        <FeesDetailsTooltip
          completed
          monetaryAmount={offerAmounts}
          marketFeeMonetaryAmount={marketFeeMonetaryAmount}
          referenceCurrency={offerAmounts.referenceCurrency}
        />
      )}
    </StyledTokenDetailsRow>
  );
};

SalePrice.fragments = {
  offer: gql`
    fragment SalePrice_offer on TokenOffer {
      id
      marketFeeAmounts {
        ...useMonetaryAmount_monetaryAmount
      }
      receiverSide {
        id
        amounts {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ...useTokenOfferBelongsToUser_offer
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${useTokenOfferBelongsToUser.fragments.offer}
  ` as TypedDocumentNode<SalePrice_offer>,
};
